import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  SettlementCycleEnum,
  SettlementRunStatusEnum,
  SettlementRunTypeEnum,
} from '@/lib/api-admin/types';

export type SettlementSort =
  | 'createdAt,desc'
  | 'createdAt,asc'
  | 'periodStart,desc'
  | 'periodStart,asc'
  | 'netPayout,desc'
  | 'netPayout,asc';

export const SETTLEMENT_SORT_VALUES: ReadonlyArray<SettlementSort> = [
  'createdAt,desc',
  'createdAt,asc',
  'periodStart,desc',
  'periodStart,asc',
  'netPayout,desc',
  'netPayout,asc',
];

export const SETTLEMENT_SORTABLE_COLUMNS = new Set([
  'createdAt',
  'periodStart',
  'netPayout',
]);

export interface SettlementFiltersState {
  vendorId: string | null;
  cycle: SettlementCycleEnum | null;
  runType: SettlementRunTypeEnum | null;
  status: ReadonlyArray<SettlementRunStatusEnum>;
  periodFrom: string | null;
  periodTo: string | null;
  page: number;
  size: number;
  sort: SettlementSort;
  setVendorId: (v: string | null) => void;
  setCycle: (v: SettlementCycleEnum | null) => void;
  setRunType: (v: SettlementRunTypeEnum | null) => void;
  setStatuses: (v: ReadonlyArray<SettlementRunStatusEnum>) => void;
  toggleStatus: (s: SettlementRunStatusEnum) => void;
  setPeriodFrom: (v: string | null) => void;
  setPeriodTo: (v: string | null) => void;
  setPage: (v: number) => void;
  setSize: (v: number) => void;
  setSort: (v: SettlementSort) => void;
  reset: () => void;
}

const INITIAL = {
  vendorId: null as string | null,
  cycle: null as SettlementCycleEnum | null,
  runType: null as SettlementRunTypeEnum | null,
  status: [] as ReadonlyArray<SettlementRunStatusEnum>,
  periodFrom: null as string | null,
  periodTo: null as string | null,
  page: 1,
  size: 20,
  sort: 'createdAt,desc' as SettlementSort,
};

export const useSettlementFiltersStore = create<SettlementFiltersState>()(
  devtools(
    (set) => ({
      ...INITIAL,
      setVendorId: (vendorId) => set({ vendorId, page: 1 }),
      setCycle: (cycle) => set({ cycle, page: 1 }),
      setRunType: (runType) => set({ runType, page: 1 }),
      setStatuses: (status) => set({ status, page: 1 }),
      toggleStatus: (s) =>
        set((state) => {
          const next = state.status.includes(s)
            ? state.status.filter((x) => x !== s)
            : [...state.status, s];
          return { status: next, page: 1 };
        }),
      setPeriodFrom: (periodFrom) => set({ periodFrom, page: 1 }),
      setPeriodTo: (periodTo) => set({ periodTo, page: 1 }),
      setPage: (page) => set({ page }),
      setSize: (size) => set({ size, page: 1 }),
      setSort: (sort) => set({ sort, page: 1 }),
      reset: () => set({ ...INITIAL }),
    }),
    { name: 'settlement-filters' },
  ),
);
